import { Box, Skeleton, Typography, useMediaQuery, useTheme } from '@mui/material';
// eslint-disable-next-line no-restricted-imports -- standalone client-side history table, no server-side pagination
import { DataGridPro } from '@mui/x-data-grid-pro';
import _ from 'lodash';
import React, { useEffect, useMemo, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { CartesianGrid, Legend, Line, LineChart, ResponsiveContainer, Tooltip, XAxis, YAxis } from 'recharts';

import { useUser } from '@sophocles/app';
import { formatDateForSubmission, formatDate, getUserMeasurementSystem } from '@sophocles/utils/formatUtils';
import { rawToF, rawToC, displayRh } from '@sophocles/utils/sensorConversions';

import { fetchSensorData } from '../../../api/vantage';

const sensorColors = [
  '#1976d2', // blue
  '#388e3c', // green
  '#fbc02d', // yellow
  '#d32f2f', // red
  '#7b1fa2', // purple
  '#0288d1', // light blue
  '#c2185b', // pink
  '#ffa000', // orange
];

function SensorDataGrid({ relayControllerId, startDate, endDate }) {
  const theme = useTheme();
  const { t } = useTranslation();
  const isMobile = useMediaQuery(theme.breakpoints.down('md'));

  // Get user's measurement system
  const { user } = useUser();
  const measurementSystem = getUserMeasurementSystem(user);

  const [data, setData] = useState([]);
  const [loading, setLoading] = useState(true);

  // Conversion helpers for raw <-> user-facing values
  const displayTemp = raw => (measurementSystem === 'imperial' ? Math.round(rawToF(raw)) : Math.round(rawToC(raw)));

  useEffect(() => {
    setLoading(true);
    fetchSensorData(relayControllerId, {
      start_date: formatDateForSubmission(startDate),
      end_date: formatDateForSubmission(endDate),
    })
      .then(data => {
        setData(data);
      })
      .finally(() => {
        setLoading(false);
      });
  }, [relayControllerId, startDate, endDate]);

  const getChartData = () => {
    const chartData = [];
    data.forEach(row => {
      row.sensor_data
        .filter(sensor => sensor.sensor_type === 2)
        .forEach(sensor => {
          chartData.push({
            date: formatDate(row.date, user),
            sensor_number: sensor.sensor_number + 1,
            temperature: sensor.temperature != 0 ? displayTemp(sensor.temperature) : null,
            humidity: sensor.humidity != 0 ? displayRh(sensor.humidity) : null,
          });
        });
    });
    return chartData.reverse(); // Reverse order so most recent is last
  };

  const renderTempChart = () => {
    const chartData = getChartData();
    const sensorNumbers = Array.from(new Set(chartData.map(d => d.sensor_number)));
    const tempUnit = measurementSystem === 'imperial' ? '°F' : '°C';
    return (
      <ResponsiveContainer width="100%" height={300}>
        <LineChart data={chartData} margin={{ top: 15, right: 30, left: 0, bottom: isMobile ? 25 : 15 }}>
          <XAxis
            dataKey="date"
            tick={{ fontSize: 10, transform: 'rotate(-45deg)', textAnchor: 'end' }}
            tickLine={false}
            axisLine={{ stroke: theme.palette.divider }}
            interval={'preserveStartEnd'}
          />
          <YAxis
            label={{
              value: `${t('general.temperature')} (${tempUnit})`,
              angle: -90,
              position: 'insideLeft',
              fontSize: 12,
            }}
            tick={{ fontSize: isMobile ? 10 : 12 }}
            tickLine={false}
            axisLine={{ stroke: theme.palette.divider }}
            width={isMobile ? 30 : 50}
          />
          <CartesianGrid strokeDasharray="3 3" stroke={theme.palette.divider} />
          <Tooltip />
          <Legend wrapperStyle={{ paddingTop: isMobile ? 10 : 20, fontSize: isMobile ? '0.8em' : '1em' }} />
          {sensorNumbers.map((num, idx) => (
            <Line
              key={`temp-${num}`}
              name={`${t('measurements.sensor')} ${num} (${tempUnit})`}
              type="monotone"
              dataKey={d => (d.sensor_number === num ? d.temperature : null)}
              stroke={sensorColors[idx % sensorColors.length]}
              strokeWidth={2}
              dot={isMobile ? false : { stroke: sensorColors[idx % sensorColors.length], strokeWidth: 2 }}
              activeDot={{ r: isMobile ? 5 : 8 }}
              connectNulls
            />
          ))}
        </LineChart>
      </ResponsiveContainer>
    );
  };

  const renderHumidityChart = () => {
    const chartData = getChartData();
    const sensorNumbers = Array.from(new Set(chartData.map(d => d.sensor_number)));
    return (
      <ResponsiveContainer width="100%" height={300}>
        <LineChart data={chartData} margin={{ top: 15, right: 30, left: 0, bottom: isMobile ? 25 : 15 }}>
          <XAxis
            dataKey="date"
            tick={{ fontSize: 10, transform: 'rotate(-45deg)', textAnchor: 'end' }}
            tickLine={false}
            axisLine={{ stroke: theme.palette.divider }}
            interval={'preserveStartEnd'}
          />
          <YAxis
            label={{ value: `${t('general.humidity')} (%)`, angle: -90, position: 'insideLeft', fontSize: 12 }}
            tick={{ fontSize: isMobile ? 10 : 12 }}
            tickLine={false}
            axisLine={{ stroke: theme.palette.divider }}
            width={isMobile ? 30 : 50}
          />
          <CartesianGrid strokeDasharray="3 3" stroke={theme.palette.divider} />
          <Tooltip />
          <Legend wrapperStyle={{ paddingTop: isMobile ? 10 : 20, fontSize: isMobile ? '0.8em' : '1em' }} />
          {sensorNumbers.map((num, idx) => (
            <Line
              key={`hum-${num}`}
              name={`${t('measurements.sensor')} ${num} (%)`}
              type="monotone"
              dataKey={d => (d.sensor_number === num ? d.humidity : null)}
              stroke={sensorColors[idx % sensorColors.length]}
              strokeWidth={2}
              dot={isMobile ? false : { stroke: sensorColors[idx % sensorColors.length], strokeWidth: 2 }}
              activeDot={{ r: isMobile ? 5 : 8 }}
              connectNulls
            />
          ))}
        </LineChart>
      </ResponsiveContainer>
    );
  };

  const tempUnit = measurementSystem === 'imperial' ? '°F' : '°C';

  // Flatten { date, sensor_data: [...] } into one row per (date, sensor) pair
  // for grid display. Only temp/humidity sensors (sensor_type === 2).
  const gridRows = useMemo(
    () =>
      data.flatMap((row, index) =>
        row.sensor_data
          .filter(sensor => sensor.sensor_type === 2)
          .map((sensor, sidx) => ({
            id: `${index}-${sidx}`,
            date: row.date,
            port: sensor.sensor_number + 1,
            temperature:
              sensor.temperature != null && sensor.temperature !== 0 ? displayTemp(sensor.temperature) : null,
            humidity: sensor.humidity != null && sensor.humidity !== 0 ? displayRh(sensor.humidity) : null,
          })),
      ),
    [data, displayTemp],
  );

  const gridColumns = useMemo(
    () => [
      {
        field: 'date',
        headerName: t('general.date'),
        flex: 1,
        minWidth: 160,
      },
      {
        field: 'port',
        headerName: `${t('general.port')} #`,
        width: 100,
      },
      {
        field: 'temperature',
        headerName: `${t('general.temperature')} (${tempUnit})`,
        width: 160,
        valueFormatter: value => (value == null ? '-' : value),
      },
      {
        field: 'humidity',
        headerName: `${t('general.humidity')} (%)`,
        width: 140,
        valueFormatter: value => (value == null ? '-' : value),
      },
    ],
    [t, tempUnit],
  );

  // Mobile list view — collapses the four-column row into a stacked card layout
  const listViewColumn = useMemo(
    () => ({
      field: 'listColumn',
      renderCell: params => {
        const { row } = params;
        return (
          <Box sx={{ py: 1, px: 0.5, width: '100%' }}>
            <Typography variant="caption" color="text.secondary" sx={{ display: 'block', mb: 0.25 }}>
              {row.date} — {t('general.port')} #{row.port}
            </Typography>
            <Typography variant="body2">
              {t('general.temperature')}: <b>{row.temperature == null ? '-' : `${row.temperature} ${tempUnit}`}</b>
            </Typography>
            <Typography variant="body2">
              {t('general.humidity')}: <b>{row.humidity == null ? '-' : `${row.humidity} %`}</b>
            </Typography>
          </Box>
        );
      },
    }),
    [t, tempUnit],
  );

  return loading ? (
    <Box sx={{ display: 'flex', flexDirection: 'column', gap: 3 }}>
      {/* Skeleton for Chart */}
      <Skeleton variant="rectangular" height={300} />

      {/* Skeleton for Table */}
      <Box>
        {[...Array(5)].map((_, index) => (
          <Skeleton key={index} variant="rectangular" height={40} sx={{ my: 1, borderRadius: 1 }} />
        ))}
      </Box>
    </Box>
  ) : (
    <Box>
      {/* Chart container: side-by-side on desktop, stacked on mobile */}
      <Box
        sx={{
          display: 'flex',
          flexDirection: { xs: 'column', xl: 'row' },
          gap: 4,
          mb: 4,
        }}
      >
        <Box sx={{ flex: 1 }}>
          <Typography variant="h6" sx={{ mb: 2 }}>
            {t('general.temperature')}
          </Typography>
          {renderTempChart()}
        </Box>
        <Box sx={{ flex: 1 }}>
          <Typography variant="h6" sx={{ mb: 2 }}>
            {t('general.humidity')}
          </Typography>
          {renderHumidityChart()}
        </Box>
      </Box>
      <Box sx={{ minHeight: 360 }}>
        <DataGridPro
          rows={gridRows}
          columns={gridColumns}
          density="compact"
          hideFooter
          disableRowSelectionOnClick
          listView={isMobile}
          listViewColumn={listViewColumn}
          localeText={{ noRowsLabel: t('general.noResultsMessage') }}
          sx={{ minHeight: 360 }}
        />
      </Box>
    </Box>
  );
}

export default SensorDataGrid;
